import React, {PropsWithChildren, useCallback, useEffect, useMemo} from "react";
import {
  getCurrentRegion,
  ObjectPreferenceEntity,
  OtsState,
  RegionEntity,
  timeZone,
  TransportCategory,
  useGetServiceConfigQuery,
  useOtsSelector,
  serviceConfigSelector,
  ServiceConfig,
} from "@ots-frontend/ots-data";
import {ActionCreatorWithPayload, Selector} from "@reduxjs/toolkit";
import {useDispatch} from "react-redux";
import {BaseQueryApi} from "@reduxjs/toolkit/query";
import type {OtsBrand, OtsColorSchemePreference} from "../theme/OtsTheme";

export type OtsDensity = "compact" | "default";

export type AppContextType = {
  regionId?: string;
  regionName?: string;
  uoms?: {
    volume?: string
    linear?: string
    temperature?: string
    odometer?: string
    meter?: string
    weight?: string
  },
  preferences?: ObjectPreferenceEntity
  getPreferences?: (category: TransportCategory) => ObjectPreferenceEntity | undefined
  dispatchRequestDuration?: number
  compliance?: {
    rrc?: boolean
  }
  appCategory?: TransportCategory
  setAppCategory?: (category: TransportCategory) => void
  serviceConfig?: ServiceConfig
  baseQueryApi: BaseQueryApi
  googleMapsApiKey?: string
  /** Which product this is. Shared Tailwind components brand themselves with it unless given a brand. */
  brand?: OtsBrand
  /** How shared Tailwind components pick light or dark unless given a scheme. */
  colorScheme?: OtsColorSchemePreference
  /** Default layout density for shared Tailwind pages and components. */
  density?: OtsDensity
};

export const AppContext = React.createContext<AppContextType>(undefined!);

export const useApp = () => {
  return React.useContext(AppContext);
};

/** Resolves an explicit density first, then the host application's default. */
export const useAppDensity = (density?: OtsDensity): OtsDensity => {
  const app = React.useContext(AppContext) as AppContextType | undefined;
  return density ?? app?.density ?? "default";
};

/**
 * @deprecated use useApp
 */
export const useRegion = useApp

export type AppContextProviderProps<S extends OtsState> = {
  getAppCategory?: Selector<any, TransportCategory | undefined>
  setAppCategory?: ActionCreatorWithPayload<TransportCategory, string>
  baseQueryApi: BaseQueryApi,
  googleMapsApiKey?: string
  brand?: OtsBrand
  colorScheme?: OtsColorSchemePreference
  density?: OtsDensity
}

export function AppContextProvider<S extends OtsState>(props: PropsWithChildren<AppContextProviderProps<S>>) {
  const {
    children,
    getAppCategory = (state: OtsState) => undefined,
    setAppCategory,
    baseQueryApi,
    googleMapsApiKey,
    brand,
    colorScheme,
    density,
  } = props;

  const dispatch = useDispatch()

  const region = useOtsSelector<RegionEntity | undefined>(getCurrentRegion);
  const appCategory = useOtsSelector<TransportCategory | undefined>(getAppCategory);

  const _setAppCategory = useCallback((category: TransportCategory) => {
    if (setAppCategory) {
      dispatch(setAppCategory(category))
    }
  }, [setAppCategory, dispatch])

  const getPreferences = useCallback((category: TransportCategory | undefined) => {
    if (!category) {
      category = appCategory
    }

    if (category) {
      return region?.objectPreferences?.find((p => p.category === category))
    }

    return undefined

  }, [appCategory, region])

  const selectServiceConfig = useMemo(() => {
    return serviceConfigSelector();
  }, []);

  const {serviceConfig} = useGetServiceConfigQuery(undefined, {
    selectFromResult: (result) => ({
      serviceConfig: selectServiceConfig(result),
    })
  });

  const value: AppContextType = {
    regionId: region?.id,
    regionName: region?.geographicName || region?.name,
    uoms: {
      volume: region?.volumeUnitOfMeasure,
      linear: region?.linearUnitOfMeasure,
      odometer: region?.odometerUnitOfMeasure,
      temperature: region?.temperatureUnitOfMeasure,
      meter: region?.meterUnitOfMeasure,
      weight: region?.weightUnitOfMeasure
    },
    preferences: getPreferences(appCategory),
    getPreferences: getPreferences,
    dispatchRequestDuration: region?.dispatchRequestDuration,
    compliance: region ? {
      rrc: region.regulatedByRrc ?? false
    } : undefined,
    appCategory: appCategory,
    setAppCategory: setAppCategory ? _setAppCategory : undefined,
    serviceConfig: serviceConfig,
    baseQueryApi: baseQueryApi,
    googleMapsApiKey: googleMapsApiKey,
    brand: brand,
    colorScheme: colorScheme,
    density: density,
  };

  useEffect(() => {
    const newTimeZone = region?.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone
    console.debug('changed timezone to ' + newTimeZone);
    timeZone.value = newTimeZone;
  }, [region?.timeZone]);

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
}
